/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @Description: Real-world use cases section with accordion-style tabs and image display
 */
import Image from 'next/image';
import React, { useMemo, useState } from 'react';

import { useImagePreload } from '@/hooks/useImagePreload';

interface UseCase {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

const useCases: UseCase[] = [
  {
    id: 'design-illustration',
    title: 'Design & Illustration',
    description:
      'Turn short prompts or rough sketches into finished art, characters, or mood boards. Many designers use AI‑generated images to explore ideas faster and try different styles before choosing one. It also helps when testing colors, lighting, or layout options.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-generator/usecase-design-illustration.webp',
  },
  {
    id: 'small-business',
    title: 'Small Business',
    description:
      'Create ads, product images, and branding materials without planning a full photoshoot. Small teams can make quick variations for testing or for new campaigns. For startups with limited time or budget, AI visuals offer a simple way to produce strong creative assets.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-generator/usecase-small-business.webp',
  },
  {
    id: 'ecommerce',
    title: 'E-commerce',
    description:
      'Produce clean product shots, lifestyle scenes, and background changes that improve listing quality. Sellers can try different looks to see which one gets more clicks or sales. Seasonal or themed images can be made without reshooting items.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-generator/usecase-ecommerce.webp',
  },
  {
    id: 'social-media',
    title: 'Social Media & Branding',
    description:
      'Transform selfies into portraits, avatars, or anime-style images in just a few steps. With AI image tools, building a unique online look or refreshing profile photos becomes much easier. Trendy styles can be explored to match current or personal taste.',
    imageUrl:
      'https://assets.falcocut.ai/ai-image-generator/usecase-social-media-branding.webp',
  },
  {
    id: 'education',
    title: 'Education',
    description:
      'Create visuals for school projects, class presentations, or personal creative ideas. Students can use AI-generated images to explain concepts in a simple, visual way. Teachers can build clearer and more engaging learning materials. Creativity becomes easy for anyone, no matter their skill level.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-generator/usecase-education.webp',
  },
];

interface UseCaseTabProps {
  useCase: UseCase;
  isActive: boolean;
  onClick: () => void;
}

const UseCaseTab: React.FC<UseCaseTabProps> = ({ useCase, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl transition-colors duration-300 p-4 pad:p-5 ${
        isActive
          ? 'bg-[#7C3AED] text-white'
          : 'bg-[#F5F5F7] text-gray-700 hover:bg-[#EBEBED]'
      }`}
    >
      <h3
        className={`font-semibold text-base pad:text-base ${isActive ? 'text-white' : 'text-gray-800'}`}
      >
        {useCase.title}
      </h3>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isActive ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-sm pad:text-base text-white/90 leading-relaxed">
            {useCase.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const UseCasesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeUseCase = useCases[activeIndex];

  // 预加载所有图片
  const imageUrls = useMemo(() => useCases.map((uc) => uc.imageUrl), []);
  useImagePreload(imageUrls);

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Creative Ways to Use AI Generated Images
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          AI-generated images unlock new possibilities for creators, businesses, and
          everyday users. Whether you&apos;re creating marketing materials, or exploring
          new artistic styles, FalcoCut helps you produce high-quality results in seconds.
          Use it to speed up your workflow, test ideas, or create polished assets without
          needing design experience.
        </p>

        {/* Content */}
        <div className="flex flex-col pad:flex-row gap-6 pad:gap-8 sm:gap-12 items-stretch">
          {/* Left - Image */}
          <div className="w-full pad:w-[55%] flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3]">
              <Image
                src={activeUseCase.imageUrl}
                alt={activeUseCase.title}
                fill
                className="object-cover transition-opacity duration-300"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Right - Tabs */}
          <div className="w-full pad:w-[45%] flex flex-col gap-3 pad:gap-4">
            {useCases.map((useCase, index) => (
              <UseCaseTab
                key={useCase.id}
                useCase={useCase}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
